import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { TransportationReport, TransportationReportForm, TransportationReportSearchForm } from 'entities/transportation_report'

type TransportationReportsResponse = PagingResponse & {
    results: TransportationReport[]
}

export function useFetchTransportationReportsApi(
    searchForm?: Form<TransportationReportSearchForm>
): IndexApiSet<TransportationReportsResponse> & { execute: () => void } {
    const apiPath = 'transportation_reports/'
    const api = useIndexApi<TransportationReportsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvTransportationReportsApi(searchForm: Form<TransportationReportSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'transportation_reports/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'transportation_reports', searchForm.object)
    }

    return { ...api, execute: execute }
}

type TransportationReportResponse = BaseResponse & TransportationReport

export function useShowTransportationReportApi(): ApiSet<TransportationReportResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<TransportationReportResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `transportation_reports/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTransportationReportApi(): ApiSet<BaseResponse> & { execute: (form: Form<TransportationReportForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TransportationReportForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TransportationReportForm>) => {
        const apiPath = `transportation_reports/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTransportationReportApi(): ApiSet<BaseResponse> & { execute: (form: Form<TransportationReportForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TransportationReportForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TransportationReportForm>) => {
        const apiPath = `transportation_reports/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTransportationReportsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TransportationReportForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TransportationReportForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TransportationReportForm[]>) => {
        const apiPath = `transportation_reports/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTransportationReportApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `transportation_reports/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
